import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Delegate pointerover inside the component to detect hovered submenu links
    this.addEventListener('pointerover', this.#handleSubmenuPointerOver, {
      signal: this.#abortController.signal,
    });

    // Delegate pointerout to detect when leaving a card and reset cards to inactive
    this.addEventListener('pointerout', this.#handleSubmenuPointerOut, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);

    // After the document is ready, annotate each mega menu group with its card count
    onDocumentLoaded(this.#annotateGroupCardCounts);

    // After document ready, bind leave handlers to cards for immediate reset-on-leave
    onDocumentLoaded(() => this.#bindCardLeaveHandlers(this));

    // Re-initialize when header is repositioned (for bottom sticky position on homepage)
    this.#watchForRepositioning();
  }

  /**
   * Watch for header repositioning and re-initialize mega menu groups
   */
  #watchForRepositioning() {
    const headerGroup = document.getElementById('header-group');
    if (!headerGroup) return;

    // Check if header has sticky-position--bottom class
    const headerComponent = this.closest('#header-component');
    if (!headerComponent?.classList.contains('header__sticky-position--bottom')) return;

    // Use MutationObserver to detect when header-group gets the 'positioned' class
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (headerGroup.classList.contains('positioned')) {
            // Re-run initialization after a small delay to ensure DOM is settled
            setTimeout(() => {
              // Abort and recreate the abort controller to reset all event listeners
              this.#abortController.abort();
              this.#abortController = new AbortController();
              
              // Reattach event listeners
              this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
                signal: this.#abortController.signal,
              });

              this.addEventListener('pointerover', this.#handleSubmenuPointerOver, {
                signal: this.#abortController.signal,
              });

              this.addEventListener('pointerout', this.#handleSubmenuPointerOut, {
                signal: this.#abortController.signal,
              });

              // Re-initialize mega menu groups
              this.#annotateGroupCardCounts();
              this.#bindCardLeaveHandlers(this);
            }, 100);
            observer.disconnect();
          }
        }
      });
    });

    observer.observe(headerGroup, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    // Clean up outside click listener
    if (this.#outsideClickController) {
      this.#outsideClickController.abort();
      this.#outsideClickController = null;
    }
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | TouchEvent} event
   */
  activate = (event) => {
    // On touch input (not just touch-capable devices), ignore pointerenter and focus events to prevent double activation
    // Use pointerType to detect actual input method: 'touch', 'pen', or 'mouse'
    const isTouchInput = event instanceof PointerEvent && event.pointerType === 'touch';
    if (isTouchInput && (event.type === 'pointerenter' || event.type === 'focus')) {
      return;
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent | TouchEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Force a reflow to ensure we're measuring the correct submenu content
    // This prevents reading stale heights from previous menu transitions
    if (submenu) {
      void submenu.offsetHeight;
    }

    // Get the actual content height from the submenu-inner element for more accurate measurement
    const submenuInner = submenu?.querySelector('.menu-list__submenu-inner');
    const contentHeight = submenuInner ? submenuInner.scrollHeight : 0;
    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight, contentHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    // When a submenu opens, set a default active link key for custom content blocks
    if (submenu) {
      const grid = submenu.querySelector('.mega-menu__grid');
      if (grid) {
        // Check if there are any custom groups to sync
        const hasCustomGroups = grid.querySelector('.mega-menu__content--groups');
        if (hasCustomGroups) {
          const firstLink = grid.querySelector('.mega-menu__list .mega-menu__link[data-link-key]');
          const key = firstLink?.getAttribute('data-link-key');
          // Call sync even without a key to show first group by default
          this.#syncMegaMenuGroups(grid, key || null);
          
          // Recalculate height after syncing groups since content may have changed
          requestAnimationFrame(() => {
            const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
            const contentHeight = submenuInner ? submenuInner.scrollHeight : 0;
            const newSubmenuHeight = Math.max(submenu.offsetHeight, overflowMenuHeight, contentHeight);
            this.style.setProperty('--submenu-height', `${newSubmenuHeight}px`);
          });
        }
      }
    }

    // Set up outside click listener for touch devices
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouch) {
      // Remove any existing listener
      if (this.#outsideClickController) {
        this.#outsideClickController.abort();
      }

      // Add new listener after a small delay to avoid immediate triggering
      this.#outsideClickController = new AbortController();
      setTimeout(() => {
        if (this.#outsideClickController) {
          document.addEventListener('click', this.#handleOutsideClick, {
            signal: this.#outsideClickController.signal,
            capture: true,
          });
        }
      }, 0);
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Handle pointerover within submenus to update which custom mega menu group is visible
   * @param {PointerEvent} event
   */
  #handleSubmenuPointerOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // If hovering a card, mark it as active in its group; otherwise do not set any default active card
    const hoveredCard = target.closest('.mega-menu__content--groups [data-mega-menu-card]');
    if (hoveredCard) {
      const group = hoveredCard.closest('[data-parent-link-key]');
      if (group) {
        const cards = group.querySelectorAll('[data-mega-menu-card]');
        // Deactivate all cards first, then activate the hovered card
        cards.forEach((c) => /** @type {HTMLElement} */ (c).setAttribute('data-mega-menu-card', 'inactive'));
        /** @type {HTMLElement} */ (hoveredCard).setAttribute('data-mega-menu-card', 'active');
      }
      return; // Do not also process link hover in this event
    }

    const link = target.closest('.mega-menu__list .mega-menu__link[data-link-key]');
    if (!link) return;

    const key = link.getAttribute('data-link-key');
    if (!key) return;

    const grid = link.closest('.mega-menu__grid');
    if (!grid) return;

    this.#syncMegaMenuGroups(grid, key);
    
    // Recalculate height after syncing to account for different group heights
    requestAnimationFrame(() => {
      if (!this.#state.activeItem) return;
      
      const activeSubmenu = findSubmenu(this.#state.activeItem);
      if (activeSubmenu) {
        const submenuInner = activeSubmenu.querySelector('.menu-list__submenu-inner');
        const contentHeight = submenuInner ? submenuInner.scrollHeight : 0;
        const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
        const newSubmenuHeight = Math.max(activeSubmenu.offsetHeight, overflowMenuHeight, contentHeight);
        this.style.setProperty('--submenu-height', `${newSubmenuHeight}px`);
      }
    });
  };

  /**
   * Handle pointerout to restore the first card active when leaving a card
   * (but do nothing if moving directly to another card).
   * @param {PointerEvent} event
   */
  #handleSubmenuPointerOut = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

  const outCard = target.closest('.mega-menu__content--groups [data-mega-menu-card]');
    if (!outCard) return;

    const related = /** @type {Element | null} */ (event.relatedTarget instanceof Element ? event.relatedTarget : null);

    // If we're still inside the same card, ignore.
    if (related && outCard instanceof Element && outCard.contains(related)) return;

    // If moving to another card within groups, do nothing (that card's pointerover will activate it)
    if (related) {
      const toCard = related.closest('.mega-menu__content--groups [data-mega-menu-card]');
      if (toCard && toCard !== outCard) return;
    }

    // Otherwise, restore the first card in this card's group to active
    const group = outCard.closest('[data-parent-link-key]');
    if (group) {
      const cards = group.querySelectorAll('[data-mega-menu-card]');
      if (cards.length > 0) {
        cards.forEach((c, i) => /** @type {HTMLElement} */ (c).setAttribute('data-mega-menu-card', i === 0 ? 'active' : 'inactive'));
      }
    }
  };

  /**
   * Given a mega menu grid and a key, show only the matching custom groups
   * @param {Element} grid
   * @param {string} key
   */
  #syncMegaMenuGroups(grid, key) {
    const groups = grid.querySelector('.mega-menu__content--groups');
    if (!groups) return;

    // Ensure pointerleave resets all cards to inactive
    this.#ensureGroupLeaveResets(groups);

    const children = groups.querySelectorAll(':scope > [data-parent-link-key]');
    
    // If no key provided or empty, show the first group by default
    let hasMatch = false;
    
    children.forEach((el, index) => {
      const groupKey = el.getAttribute('data-parent-link-key');
      const match = (key && groupKey === key) || (!key && index === 0);
      if (match) hasMatch = true;
      /** @type {HTMLElement} */
      const node = /** @type {any} */ (el);
      node.style.display = match ? '' : 'none';
      // Reset cards in all groups to inactive when switching active link key
      const cardsInGroup = node.querySelectorAll('[data-mega-menu-card]');
      cardsInGroup.forEach((c) => /** @type {HTMLElement} */ (c).setAttribute('data-mega-menu-card', 'inactive'));
    });

    // Dynamically compute total columns as (link columns) + (card blocks in active group)
    const list = grid.querySelector('[data-menu-list-id]');
    // Prefer computing actual link columns by summing column spans in the list
    let linkCols = 0;
    if (list) {
      const cols = list.querySelectorAll(':scope > li.mega-menu__column');
      cols.forEach((col) => {
        const match = Array.from(col.classList).find((c) => c.startsWith('mega-menu__column--span-'));
        const span = match ? parseInt(match.replace('mega-menu__column--span-', ''), 10) : 1;
        linkCols += isNaN(span) ? 1 : span;
      });
    }
    // Fallback to data attribute if no columns found
    if (!linkCols && list) linkCols = parseInt(list.getAttribute('data-link-column-count') || '0', 10);

    const activeGroupCards = groups.querySelectorAll(`:scope > [data-parent-link-key="${key}"] [data-mega-menu-card]`).length
      || groups.querySelectorAll(`:scope > [data-parent-link-key="${key}"]`).length;

    // Expose active group's card count via data attribute and set grid placement
    const activeGroupEl = groups.querySelector(`:scope > [data-parent-link-key="${key}"]`);
    const activeCount = Math.max(1, activeGroupCards);
    if (activeGroupEl instanceof HTMLElement) {
      activeGroupEl.setAttribute('data-mega-menu-card-count', String(activeCount));
      // Ensure the first card is active by default in the active group
      const cards = activeGroupEl.querySelectorAll('[data-mega-menu-card]');
      if (cards.length > 0) {
        cards.forEach((c, i) => /** @type {HTMLElement} */ (c).setAttribute('data-mega-menu-card', i === 0 ? 'active' : 'inactive'));
      }
    }
    // Set the content container (.mega-menu__content) to span [value] columns ending at the last grid line
    if (groups instanceof HTMLElement) {
      groups.setAttribute('data-active-card-count', String(activeCount));
      // Compute total column count to determine if we should introduce a 1-column gap between links and group
      // totalCols is computed below, but we need it here for effective span; we will recompute locally using linkCols + activeGroupCards
      const projectedTotalCols = Math.min(12, Math.max(1, linkCols + activeGroupCards));
      let effectiveSpan = activeCount;
      if (projectedTotalCols > 6 && activeCount > 3) {
        // Leave exactly 1 column gap by reducing the group's span by 1
        effectiveSpan = Math.max(1, activeCount - 1);
      }
      groups.style.gridColumn = `span ${effectiveSpan} / -1`;
    }

    const totalCols = Math.min(12, Math.max(1, linkCols + activeGroupCards));
    // Expose total column count on the mega menu grid element
    if (grid instanceof Element) {
      grid.setAttribute('data-total-columns', String(totalCols));
    }
    // Apply to grid container so CSS can pick it up if needed
    /** @type {HTMLElement} */ (grid).style.setProperty('--menu-columns-desktop', String(totalCols));
    // Keep the list width equal to its actual link column count so content aligns on the same row
    if (list instanceof HTMLElement) list.style.setProperty('--menu-columns-desktop', String(Math.max(1, linkCols)));

    // Ensure per-card leave handlers are bound in current context
    this.#bindCardLeaveHandlers(groups);
  }

  /**
   * Ensure cards reset to inactive when pointer leaves the groups container.
   */
  #ensureGroupLeaveResets(groups) {
    if (!(groups instanceof HTMLElement)) return;
    if (groups.dataset.leaveResetBound === 'true') return;
    groups.dataset.leaveResetBound = 'true';
    groups.addEventListener('pointerleave', () => {
      // On leaving the groups container, restore the first card in the active group to active
      const activeKey = groups.getAttribute('data-active-link-key');
      /** @type {HTMLElement | null} */
      let container = null;
      if (activeKey) {
        container = groups.querySelector(`:scope > [data-parent-link-key="${activeKey}"]`);
      }
      if (!(container instanceof HTMLElement)) {
        // Fallback to first visible group, then any group
        container = /** @type {HTMLElement | null} */ (groups.querySelector(':scope > [data-parent-link-key]:not([style*="display: none"])'))
          || /** @type {HTMLElement | null} */ (groups.querySelector(':scope > [data-parent-link-key]'));
      }
      if (container) {
        const cards = container.querySelectorAll('[data-mega-menu-card]');
        cards.forEach((c, i) => /** @type {HTMLElement} */ (c).setAttribute('data-mega-menu-card', i === 0 ? 'active' : 'inactive'));
      }
    }, { signal: this.#abortController.signal });
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent | TouchEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item] - The item to deactivate (defaults to active item)
   * @param {boolean} [force=false] - Whether to force deactivation, bypassing hover checks
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Skip hover check if forcing deactivation
    if (!force && this.overflowHovered) return;

    // Clear any stuck focus/hover states when forcing
    if (force) {
      this.refs.overflowMenu?.blur();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }

    // Clean up outside click listener
    if (this.#outsideClickController) {
      this.#outsideClickController.abort();
      this.#outsideClickController = null;
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handles touchstart events on menu items.
   * On mobile, prevents navigation on first tap for items with submenus.
   * @param {TouchEvent} event - The touchstart event
   */
  handleTouchStart = (event) => {
    if (!(event.target instanceof Element)) return;

    // Find the menu item and check if it has a submenu
    const menuItem = findMenuItem(event.target);
    if (!menuItem) return;

    const hasSubmenu = menuItem.getAttribute('aria-haspopup') === 'true';
    const isMoreButton = event.target.slot === 'more';

    if (hasSubmenu || isMoreButton) {
      if (menuItem !== this.#state.activeItem) {
        // First tap: open submenu, prevent navigation
        event.preventDefault();
        this.activate(event);
      } else if (event.target.slot !== 'overflow') {
        // Already open: toggle closed
        event.preventDefault();
        this.#deactivate(this.#state.activeItem, true);
      }
    }
  };

  /**
   * Handles clicks outside the menu to close it.
   * Particularly useful for touch devices where users expect to close by tapping outside.
   * @param {MouseEvent | PointerEvent} event - The click event
   */
  #handleOutsideClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const clickedInsideMenu = this.contains(event.target);

    const clickedInsideOverflow =
      this.overflowMenu?.contains(event.target) || this.refs.overflowMenu?.contains(event.target);

    let clickedInsideSubmenu = false;
    if (this.#state.activeItem) {
      const activeSubmenu = findSubmenu(this.#state.activeItem);
      if (activeSubmenu) {
        clickedInsideSubmenu = activeSubmenu.contains(event.target);
      }
    }

    if (!clickedInsideMenu && !clickedInsideOverflow && !clickedInsideSubmenu) {
      this.#deactivate(this.#state.activeItem, true);
    }
  };

  /**
   * AbortController for managing outside click listeners
   * @type {AbortController | null}
   */
  #outsideClickController = null;

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Annotate each mega menu group with a data attribute indicating how many cards it contains.
   * Helps styling and layout decisions without re-counting repeatedly.
   */
  #annotateGroupCardCounts = () => {
    const groupContainers = this.querySelectorAll('.mega-menu__content--groups');
    groupContainers.forEach((container) => {
      const groups = container.querySelectorAll(':scope > [data-parent-link-key]');
      groups.forEach((group) => {
        if (!(group instanceof HTMLElement)) return;
        const count = group.querySelectorAll('[data-mega-menu-card]').length || 1;
        group.setAttribute('data-mega-menu-card-count', String(count));
        // Ensure the first card is set active by default
        const cards = group.querySelectorAll('[data-mega-menu-card]');
        if (cards.length > 0) {
          cards.forEach((c, i) => /** @type {HTMLElement} */ (c).setAttribute('data-mega-menu-card', i === 0 ? 'active' : 'inactive'));
        }
      });

      // Bind per-card leave handlers within this container
      this.#bindCardLeaveHandlers(container);
    });
  };

  /**
   * Bind pointerleave handlers to each card so that leaving a card
   * immediately resets all cards in that group to inactive.
   * @param {ParentNode} root
   */
  #bindCardLeaveHandlers(root) {
    const cards = root.querySelectorAll?.('[data-mega-menu-card]');
    cards?.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      if (card.dataset.leaveBound === 'true') return;
      card.dataset.leaveBound = 'true';
      card.addEventListener(
        'pointerleave',
        () => {
          const group = card.closest('[data-parent-link-key]');
          if (!group) return;
          const siblings = group.querySelectorAll('[data-mega-menu-card]');
          siblings.forEach((s, i) => /** @type {HTMLElement} */ (s).setAttribute('data-mega-menu-card', i === 0 ? 'active' : 'inactive'));
        },
        { signal: this.#abortController.signal }
      );
    });
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
